import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  ListGroup,
  Button,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { debounce } from "lodash";
import { searchPatients, createSession } from "./api";
import CreatePatientModal from "./CreatePatientModal";
import { toast } from "react-toastify";
import { Patient } from "../../types";

interface PatientSearchResult {
  id: string;
  animalName?: string;
  name?: string;
  parentName: string;
  updatedAt: string;
}

interface Props {
  show: boolean;
  onHide: () => void;
  deviceId: string;
}

const PatientLookUpAutocompleteModal: React.FC<Props> = ({
  show,
  onHide,
  deviceId,
}) => {
  const [query, setQuery] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [patients, setPatients] = useState<PatientSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useRef(
    debounce(async (text: string) => {
      if (text.length < 3) return;
      setLoading(true);
      try {
        const results = await searchPatients(text);
        setPatients(results);
      } finally {
        setLoading(false);
      }
    }, 500)
  ).current;

  const wait = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  useEffect(() => {
    if (query.length >= 3) debouncedSearch(query);
    else setPatients([]);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!patients.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev === null || prev === patients.length - 1 ? 0 : prev + 1
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev === null || prev === 0 ? patients.length - 1 : prev - 1
      );
    } else if (e.key === "Enter" && selectedIndex !== null) {
      handleSelect(patients[selectedIndex]);
    }
  };

  const handleSelect = async (patient: PatientSearchResult) => {
    await createSession({ patientId: patient.id, deviceId });
    setDisabled(true);
    toast.success(`Session started for ${patient.animalName || patient.name || 'patient'}`);
    await wait(2000);
    onHide();
  };

  const footerClick = () => {
    setShowCreateModal(true);
  };

  const handleCreateSuccess = async (patientData: Patient) => {
    setShowCreateModal(false);
    toast.success("Patient created");
    await createSession({ patientId: patientData.id, deviceId });
    toast.success(`Session started for ${patientData.name}`);
    onHide()
  };

  return (
    <>
      <Modal show={show} onHide={onHide} onEntered={() => inputRef.current?.focus()} centered>
        <Modal.Header closeButton>
          <Modal.Title>Find a Patient</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup>
            <Form.Control
              ref={inputRef}
              type="text"
              placeholder="Search by animal or owner name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
            {loading && (
              <InputGroup.Text>
                <Spinner animation="border" size="sm" />
              </InputGroup.Text>
            )}
          </InputGroup>
          <ListGroup className="mt-3">
            {patients.map((p, i) => (
              <ListGroup.Item
                key={p.id}
                action
                active={i === selectedIndex}
                onClick={() => handleSelect(p)}
              >
                <div><strong>{p.animalName}</strong></div>
                <div className="text-muted small">Owner: {p.parentName}</div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={footerClick}>
            + Create New Patient
          </Button>
        </Modal.Footer>
      </Modal>

      <CreatePatientModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreateSuccess={handleCreateSuccess}
      />
    </>
  );
};

export default PatientLookUpAutocompleteModal;